import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Tracks which section is currently visible in the viewport.
 * Uses IntersectionObserver with a top-biased rootMargin so the
 * section that is near the top of the viewport wins.
 *
 * Re-scans the DOM periodically until all expected sections are found
 * (sections render asynchronously after data loads).
 */
export function useScrollSpy(sectionIds: string[], offset = 100): string {
  const [active, setActive] = useState(sectionIds[0] ?? '')
  const ratios = useRef<Map<string, number>>(new Map())
  const observerRef = useRef<IntersectionObserver | null>(null)

  const setupObserver = useCallback(() => {
    observerRef.current?.disconnect()
    ratios.current.clear()

    const elements = sectionIds
      .map(id => document.getElementById(id))
      .filter(Boolean) as HTMLElement[]
    if (!elements.length) return false

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          ratios.current.set(entry.target.id, entry.intersectionRatio)
        }
        // Pick the first visible section (document order) to avoid jumps
        for (const el of elements) {
          const r = ratios.current.get(el.id) ?? 0
          if (r > 0) {
            setActive(el.id)
            return
          }
        }
      },
      { rootMargin: `-${offset}px 0px -40% 0px`, threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] },
    )

    for (const el of elements) observer.observe(el)
    observerRef.current = observer
    return elements.length === sectionIds.length
  }, [sectionIds, offset])

  useEffect(() => {
    // Initial setup — retry until all sections are in the DOM
    if (setupObserver()) return () => observerRef.current?.disconnect()

    const interval = setInterval(() => {
      if (setupObserver()) clearInterval(interval)
    }, 500)

    return () => {
      clearInterval(interval)
      observerRef.current?.disconnect()
    }
  }, [setupObserver])

  return active
}
